import { NavLink } from 'react-router-dom';
import type { IconProps } from './icons';

export interface Tab {
  to: string;
  label: string;
  Icon: (props: IconProps) => React.ReactElement;
  /** Matches only the exact path — used for the trip's index route. */
  end?: boolean;
}

/**
 * The bottom navigation. Fixed, because this is installed to a home screen and
 * used one-handed while someone is holding a bowl of noodles.
 *
 * The safe-area padding is what keeps it above the iPhone home indicator; the
 * matching padding-bottom on the page content is in TripPage.
 */
export function TabBar({ tabs }: { tabs: Tab[] }) {
  return (
    <nav
      aria-label="Trip sections"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
        display: 'flex',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--bg) 92%, transparent)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--border)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div style={{ display: 'flex', width: '100%', maxWidth: 560 }}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            style={({ isActive }) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              padding: '10px 0 12px',
              textDecoration: 'none',
              color: isActive ? 'var(--accent)' : 'var(--text-muted)',
            })}
          >
            {({ isActive }) => (
              <>
                <tab.Icon size={22} weight={isActive ? 1.9 : 1.5} />
                <span style={{ fontSize: 'var(--fs-caption)', fontWeight: isActive ? 600 : 400 }}>
                  {tab.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
